/**
 * Error Pages for Y GO Shuttle Services France
 * LOT 8 - Pages d'erreur propres (403, 404, 500)
 */
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Home, ArrowLeft, ShieldX, Search, ServerCrash } from 'lucide-react';
import { BrandMark } from '../components/BrandMark';

const ErrorPage = ({ errorCode, title, message }) => {
  const location = useLocation();
  
  // Determine error type from props or URL
  const code = errorCode || location.state?.errorCode || '404';
  
  const errorConfig = {
    '403': {
      icon: ShieldX,
      title: 'Accès refusé',
      message: 'Vous n\'avez pas les permissions nécessaires pour accéder à cette page.',
      color: 'text-red-400'
    },
    '404': {
      icon: Search,
      title: 'Page non trouvée',
      message: 'La page que vous recherchez n\'existe pas ou a été déplacée.',
      color: 'text-yellow-400'
    },
    '500': {
      icon: ServerCrash,
      title: 'Erreur serveur',
      message: 'Une erreur technique s\'est produite. Veuillez réessayer plus tard.',
      color: 'text-orange-400'
    }
  };

  const config = errorConfig[code] || errorConfig['404'];
  const IconComponent = config.icon;

  return (
    <div className="min-h-screen bg-[#0B0B0B] flex flex-col" data-testid={`error-${code}`}>
      {/* Header */}
      <header className="bg-[#1F1F1F] border-b border-[#C9A24D]/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <Link to="/" className="flex items-center gap-2">
              <BrandMark size="lg" />
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          {/* Error Code */}
          <div className="mb-6">
            <span className={`text-8xl font-bold ${config.color} opacity-20`}>
              {code}
            </span>
          </div>
          
          {/* Icon */}
          <div className="mb-6 flex justify-center">
            <div className={`p-4 rounded-full bg-[#1F1F1F] border-2 border-${config.color.split('-')[1]}-500/30`}>
              <IconComponent className={`w-12 h-12 ${config.color}`} />
            </div>
          </div>
          
          {/* Title & Message */}
          <h2 className="text-2xl font-semibold text-[#E5E5E5] mb-3">
            {title || config.title}
          </h2>
          <p className="text-[#E5E5E5]/60 mb-8">
            {message || config.message}
          </p>
          
          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              onClick={() => window.history.back()}
              variant="outline"
              className="border-[#E5E5E5]/20 text-[#E5E5E5]"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </Button>
            <Link to="/">
              <Button className="bg-[#C9A24D] text-[#0B0B0B] hover:bg-[#C9A24D]/90 w-full sm:w-auto">
                <Home className="w-4 h-4 mr-2" />
                Accueil
              </Button>
            </Link>
          </div>
          
          {/* Help text */}
          <p className="mt-8 text-[#E5E5E5]/40 text-sm">
            Si le problème persiste, contactez-nous à{' '}
            <a href="mailto:contact@y-go.fr" className="text-[#C9A24D] hover:underline">
              contact@y-go.fr
            </a>
          </p>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-[#C9A24D]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <p className="text-center text-[#E5E5E5]/40 text-sm">
            © 2024 Y GO Shuttle Services France
          </p>
        </div>
      </footer>
    </div>
  );
};

// Export specific error pages
export const Error403 = () => <ErrorPage errorCode="403" />;
export const Error404 = () => <ErrorPage errorCode="404" />;
export const Error500 = () => <ErrorPage errorCode="500" />;

export default ErrorPage;
